import {
  SubscriptionFlowDefinition,
  toIsoDate,
  type FlowAnswers,
  type FlowMetadataInput,
  type FlowPeriod,
  type FlowPeriodInput,
  type PriceTag,
  type RecapPolicy,
} from './SubscriptionFlowDefinition'
import type { AvailableBundlesParams, Bundle } from '@/services/subscription'

/**
 * Study-abroad journey (`/etudier-a-letranger`, subcategory PME).
 *
 * The decision tree consults only the destination (Europe vs outside Europe).
 * The recommended pack (Europe / World) is flagged by the backend. Unlike the
 * duration-based flows, the subscription period is the stay itself — a date
 * range (start date → end of stay) — so `period()` is overridden.
 */

/** Front `destination` answer → backend `destination` param (DecisionTree::Pme). */
const DESTINATION_TO_API: Record<string, AvailableBundlesParams['destination']> = {
  europe: 'europe',
  outsideEurope: 'monde',
}

export class AbroadFlow extends SubscriptionFlowDefinition {
  readonly product = 'abroad' as const
  readonly subcategory = 'PME' as const

  /** The cover is the stay itself (a fixed date range), so it does not renew. */
  readonly tacitRenewal = false

  /** And because it insures one stay rather than a period of life, holding one says nothing
   *  about needing another: a second trip, or an extension of the current one, is a second
   *  contract (SI-1477). */
  readonly allowsRepeatSubscription = true

  /** The decision tree already picks the pack (Europe vs World), so the results
   *  screen shows only the backend-recommended bundle. */
  readonly recommendedOnly = true

  /** Fixed-stay cover paid in one go: no payment-mode selector, no tacit-renewal
   *  row, and the total is worded against the stay (a date range). */
  readonly recap: RecapPolicy = {
    showPaymentSelector: false,
    showClub: false,
    tacitRow: 'hidden',
    priceMode: 'stay',
  }

  /** Source of truth: DecisionTree::Pme on the backend. */
  readonly planReference: Record<string, number> = {
    packEurope: 27,
    packMonde: 28,
  }

  bundlesParams(a: FlowAnswers): AvailableBundlesParams | null {
    if (!a.destination) return null
    return { subcategory: 'PME', destination: DESTINATION_TO_API[a.destination] }
  }

  /** Travel packs leave monthly_price/annual_price null and carry the stay rate
   *  in `priceInternational`; the yearly figure is that rate × 12 (for display
   *  only — the real total comes from compute_price over the stay period). */
  displayPrice(bundle: Bundle): PriceTag {
    const monthly = bundle.priceInternational
    return { perMonth: monthly, perYear: monthly != null ? monthly * 12 : null }
  }

  /** The covered period is the stay: start date → end of stay (a date range). */
  period(input: FlowPeriodInput): FlowPeriod | null {
    if (!input.startDate || !input.stayEnd) return null
    return { effectiveFrom: toIsoDate(input.startDate), effectiveUntil: toIsoDate(input.stayEnd) }
  }

  /** The contract carries the ISO code of the chosen destination country. */
  contractMetadata(_answers: FlowAnswers, input: FlowMetadataInput): Record<string, string> | null {
    if (!input.destinationCountry) return null
    return { country_of_destination: input.destinationCountry }
  }
}
